"use client";
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { useTranslations } from "next-intl";

import IconDown from "@/icons/arrow_down-red.svg";
import IconActive from "@/icons/ui/buttons/activeBtn.svg";
import FormWrapper from "@/ui/Forms/FormWrapper";
import { cn } from "@/utils/cn";

export interface SelectOption {
  value: string;
  label: string | ReactNode;
  icon?: ReactNode;
}

export interface SelectOptionStringify extends SelectOption {
  label: string;
}

export interface FormSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  error?: string;
  placeholder?: string;
  required?: boolean;
  displayMode?: "dropdown" | "buttons" | "pills";
  labelRightComponent?: ReactNode;
  style?: CSSProperties;
  className?: string;
  errorClassName?: string;
  disabled?: boolean;
  name?: string;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  value,
  onChange,
  options,
  label,
  error,
  placeholder,
  required = false,
  displayMode = "dropdown",
  labelRightComponent,
  style,
  name,
  disabled = false,
  className,
  errorClassName,
}) => {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption
    ? selectedOption.label
    : placeholder || t("Select");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // If display mode is buttons or pills, dropdown is not needed
  if (displayMode === "buttons") {
    return (
      <FormWrapper
        label={label}
        error={error}
        required={required}
        labelRight={labelRightComponent}
        errorClassName={errorClassName}
      >
        <div
          className={cn("form-select-buttons-container", className)}
          style={style}
        >
          {options.map((option) => (
            <button
              key={option.value}
              className={cn(
                "form-select-button-option",
                value === option.value && "active",
              )}
              onClick={() =>
                onChange(option.value === value ? "" : option.value)
              }
              type="button"
            >
              {option.label}
              {value === option.value && (
                <IconActive className="form-select-active-icon" />
              )}
            </button>
          ))}
        </div>
      </FormWrapper>
    );
  }

  if (displayMode === "pills") {
    return (
      <FormWrapper
        label={label}
        error={error}
        required={required}
        labelRight={labelRightComponent}
        errorClassName={errorClassName}
      >
        <div
          className={cn("form-select-pills-container", className)}
          style={style}
        >
          {options.map((option) => (
            <button
              key={option.value}
              className={cn(
                "form-select-pill-option",
                value === option.value && "active",
              )}
              onClick={() =>
                onChange(option.value === value ? "" : option.value)
              }
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </FormWrapper>
    );
  }

  // Standard dropdown list
  return (
    <FormWrapper
      label={label}
      error={error}
      required={required}
      labelRight={labelRightComponent}
      errorClassName={errorClassName}
    >
      <div
        className={cn("form-select-container", className)}
        ref={selectRef}
        style={style}
      >
        <div
          className={cn("form-select-button", className)}
          onClick={() => {
            if (disabled) {
              setIsOpen(false);
              return
            }

            setIsOpen(!isOpen);
          }}
        >
          <span className="form-select-text">{displayText}</span>
          <IconDown className={cn("form-select-arrow", isOpen && "open")} />
        </div>

        {isOpen && (
          <div className="form-select-options-container">
            {options.map((option) => (
              <div
                key={option.value}
                className={cn(
                  "form-select-option",
                  value === option.value && "selected",
                )}
                onClick={() => {
                  onChange(option.value === value ? "" : option.value);
                  setIsOpen(false);
                }}
              >
                {option.icon && (
                  <span className="form-select-option-icon">{option.icon}</span>
                )}
                <span>{option.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </FormWrapper>
  );
};

export default FormSelect;
